/**
 * Доля ядра публичного рендерера: ряд «назад» + «далее» в блоке
 * `button_next` (задание stylepack/next-back-pair, слово владельца
 * 21.10.2026, Mojo: пилюля «назад» 74×60 слева, через промежуток «Next» на
 * остальную ширину).
 *
 * Ядро (`PublicBlockRenderer.tsx`, ветка `button_next`) зовёт этот модуль,
 * передавая готовую кнопку «далее» детьми и её стиль. Ряд рисуется, только
 * когда у блока `backBeside: true` И визард дал обработчик «назад» — на
 * первом экране воронки и на холсте редактора обработчика нет, и тогда
 * отдаётся одна кнопка «далее» без единой обёртки (разметка прежняя).
 *
 * Лежит в `core-blocks/`, а не в `public-blocks/`: тот каталог — у ленивого
 * хвоста (WEB-2920), а ряд едет в первом экране вместе с ядром.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { EditorBlock } from '@/store/editorSlice';
import { nextBackPairStyles, readNextBackPair } from '../nextBackPair';

interface NextBackPairProps {
  block: EditorBlock;
  /** Обработчик «назад» визарда; нет — ряда нет. */
  onBack?: () => void;
  /** Подпись для читалки на языке воронки (`publicStrings.backLabel`). */
  backLabel: string;
  /** Итоговый стиль кнопки «далее»: от него цвет и скругление по умолчанию. */
  nextStyle: CSSProperties;
  /** Сама кнопка «далее». */
  children: ReactNode;
}

export function NextBackPair({
  block,
  onBack,
  backLabel,
  nextStyle,
  children,
}: NextBackPairProps) {
  const look = onBack ? readNextBackPair(block) : null;
  if (!look) return <>{children}</>;
  const styles = nextBackPairStyles(look, nextStyle);
  return (
    <div data-testid="next-back-pair" style={styles.row}>
      <button
        type="button"
        data-testid="next-back-button"
        aria-label={backLabel}
        onClick={onBack}
        // Как у «далее»: нажатие не отбирает фокус у поля ввода, иначе
        // focusout включил бы авто-переход раньше «назад»
        // (keepFieldFocusOnPress в QuizScreenWizard).
        onMouseDown={(e) => e.preventDefault()}
        style={styles.back}
      >
        <span aria-hidden="true">←</span>
      </button>
      <div style={styles.slot}>{children}</div>
    </div>
  );
}
